import Section from 'components/Section';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Head from 'next/head';

const GUIDE_META = {
    '/guide': {
        title: 'Getting Started Guide | tabExtend',
        description:
            'Install tabExtend and learn the core workflow for saving tabs, organizing workspaces, and staying focused in your browser.'
    },
    '/guide/getting-started': {
        title: 'Getting Started Guide | tabExtend',
        description:
            'Install tabExtend and learn the core workflow for saving tabs, organizing workspaces, and staying focused in your browser.'
    },
    '/guide/overview': {
        title: 'Dashboard Overview Guide | tabExtend',
        description:
            'Get an overview of the tabExtend dashboard, including groups, workspaces, and the main productivity tools.'
    },
    '/guide/shortcuts': {
        title: 'Keyboard Shortcuts Guide | tabExtend',
        description:
            'Use keyboard shortcuts in tabExtend to save tabs faster, navigate workspaces, and speed up your workflow.'
    },
    '/guide/create-account': {
        title: 'Create Account Guide | tabExtend',
        description:
            'Create a tabExtend account to sync your data, secure your workspaces, and access your setup across browsers.'
    },
    '/guide/manage-account': {
        title: 'Manage Account Guide | tabExtend',
        description:
            'Manage your tabExtend account details, subscription, and settings from one place.'
    },
    '/guide/popover': {
        title: 'Popover Guide | tabExtend',
        description:
            'Learn how to use the tabExtend popover for quick actions and faster tab organization.'
    },
    '/guide/mobile': {
        title: 'Mobile App Guide | tabExtend',
        description:
            'Learn what tabExtend mobile supports and how to keep your browsing workflow connected across devices.'
    },
    '/guide/trial-saves': {
        title: 'Trial Plan Guide | tabExtend',
        description:
            'Understand what is included in the tabExtend trial plan and how save limits work.'
    },
    '/guide/create-new-group': {
        title: 'Create New Group Guide | tabExtend',
        description:
            'Create new groups in tabExtend to organize open tabs by project, context, or priority.'
    },
    '/guide/edit-group': {
        title: 'Edit Group Guide | tabExtend',
        description:
            'Edit group names, icons, position, and structure to keep your tab workflow clean and flexible.'
    },
    '/guide/open-all-sites': {
        title: 'Open All Sites Guide | tabExtend',
        description:
            'Open all sites in a saved group with one click when you need to resume a full context quickly.'
    },
    '/guide/group-options': {
        title: 'Group Options Guide | tabExtend',
        description:
            'Use group options in tabExtend to manage, sort, lock, and customize your saved groups.'
    },
    '/guide/categories': {
        title: 'Categories Guide | tabExtend',
        description:
            'Use categories to structure your saved groups and keep related workspaces easy to navigate.'
    },
    '/guide/share-categories': {
        title: 'Share Categories Guide | tabExtend',
        description:
            'Share categories in tabExtend so collaborators can access grouped links and context.'
    },
    '/guide/workspaces': {
        title: 'Workspaces Guide | tabExtend',
        description:
            'Organize projects with tabExtend workspaces and switch context without tab clutter.'
    },
    '/guide/share-workspaces': {
        title: 'Share Workspaces Guide | tabExtend',
        description:
            'Share workspaces with teammates to collaborate on grouped tabs, notes, and resources.'
    },
    '/guide/save-tabs': {
        title: 'Save Tabs Guide | tabExtend',
        description:
            'Save tabs in tabExtend with drag-and-drop or one-click actions to clear clutter without losing context.'
    },
    '/guide/save-images': {
        title: 'Save Images Guide | tabExtend',
        description:
            'Save images directly into tabExtend groups to keep visuals and references tied to your projects.'
    },
    '/guide/create-note': {
        title: 'Notes and To-Dos Guide | tabExtend',
        description:
            'Create notes and to-dos in tabExtend to keep actions and context with your saved tabs.'
    },
    '/guide/reminders': {
        title: 'Reminders Guide | tabExtend',
        description:
            'Set reminders for tabs, notes, and tasks in tabExtend so nothing gets lost or delayed.'
    },
    '/guide/combine': {
        title: 'Combine Items Guide | tabExtend',
        description:
            'Combine items in tabExtend to consolidate related tabs, notes, and resources into cleaner workflows.'
    },
    '/guide/save-text-snippet': {
        title: 'Save Text Snippets Guide | tabExtend',
        description:
            'Capture and store text snippets in tabExtend so key details stay attached to your browsing context.'
    },
    '/guide/bookmarks': {
        title: 'Import Bookmarks Guide | tabExtend',
        description:
            'Import bookmarks into tabExtend and organize them into groups and workspaces for better retrieval.'
    },
    '/guide/search': {
        title: 'Search Guide | tabExtend',
        description:
            'Search across saved tabs, groups, and notes in tabExtend to find what you need fast.'
    },
    '/guide/settings': {
        title: 'Settings Guide | tabExtend',
        description:
            'Customize tabExtend settings for themes, sounds, shortcuts, and behavior to match your workflow.'
    },
    '/guide/custom-emojis': {
        title: 'Custom Emojis Guide | tabExtend',
        description:
            'Use custom emojis in tabExtend to label groups and workspaces with visual cues.'
    },
    '/guide/bin': {
        title: 'Bin Guide | tabExtend',
        description:
            'Restore or permanently remove deleted items from the tabExtend bin.'
    }
};

const DEFAULT_GUIDE_META = {
    title: 'User Guide | tabExtend',
    description:
        'Learn how to use tabExtend to manage tabs, save context, and organize browser workflows.'
};

const NavLink = ({ href, linkName, sublinks, mobile }) => {
    const router = useRouter();
    const active = router.pathname === href ? true : false;

    return (
        <Link
            href={href}
            style={
                active
                    ? {
                          fontWeight: '600',
                          marginLeft: mobile ? '0' : '-11px',
                          marginRight: '24px',
                          whiteSpace: 'nowrap',
                          color: '#1A202C'
                      }
                    : {
                          marginRight: '24px',
                          whiteSpace: 'nowrap',
                          color: '#1A202C'
                      }
            }
        >
            {active ? (
                <>
                    ▸ {linkName}
                    {sublinks && (
                        <ul style={{ borderLeft: '1px solid #b4b8c1' }}>
                            {sublinks.map((item, index) => (
                                <li
                                    key={index}
                                    onClick={() => {
                                        router.push(
                                            router.pathname + '/#' + item.title
                                        );
                                    }}
                                >
                                    {item.title}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            ) : (
                linkName
            )}
        </Link>
    );
};

export default function SideBar({ children }) {
    const router = useRouter();
    const currentPath = router.pathname;
    const pageMeta = GUIDE_META[currentPath] || DEFAULT_GUIDE_META;
    const canonicalUrl = `https://www.tabextend.com${currentPath}`;
    const guideLabel = currentPath
        .replace('/guide', '')
        .replace('/', '')
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    const currentGuideName = guideLabel.length > 0 ? guideLabel : 'Getting Started';
    const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                name: pageMeta.title,
                description: pageMeta.description,
                url: canonicalUrl,
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'tabExtend',
                    url: 'https://www.tabextend.com'
                }
            },
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Guide',
                        item: 'https://www.tabextend.com/guide'
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: currentGuideName,
                        item: canonicalUrl
                    }
                ]
            }
        ]
    };

    return (
        <>
            <Head>
                <title>{pageMeta.title}</title>
                <meta name="description" content={pageMeta.description} />
                <meta property="og:title" content={pageMeta.title} />
                <meta property="og:description" content={pageMeta.description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href={canonicalUrl} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(structuredData)
                    }}
                />
            </Head>
            <section className="SectionComponent hero section is-block is-relative py-8">
                <div className="container has-text-left">
                    <div
                        style={{
                            backgroundColor: '#F4F5F7',
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'sticky',
                            top: '0',
                            zIndex: 100,
                            paddingBottom: '16px',
                            paddingTop: '12px'
                        }}
                        className="sideBarMobile is-hidden-desktop"
                    >
                        <div
                            style={{
                                width: '100%',
                                height: '58px',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'scroll',
                                paddingTop: '16px',
                                paddingBottom: '16px',
                                position: 'relative'
                            }}
                        >
                            <NavLink
                                href="/guide/getting-started"
                                linkName={'Getting started'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/overview"
                                linkName={'Overview'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/shortcuts"
                                linkName={'Shortcuts'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/create-account"
                                linkName={'Create account'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/manage-account"
                                linkName={'Manage account'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/popover"
                                linkName={'The Popover'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/mobile"
                                linkName={'Mobile app'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/trial-saves"
                                linkName={'Trial plan'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/create-new-group"
                                linkName={'Create new group'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/edit-group"
                                linkName={'Edit group'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/group-options"
                                linkName={'Group options'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/categories"
                                linkName={'Categories'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/share-categories"
                                linkName={'Share categories'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/workspaces"
                                linkName={'Workspaces'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/share-workspaces"
                                linkName={'Share workspaces'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/save-tabs"
                                linkName={'Save tabs'}
                                mobile={true}
                            ></NavLink> 
                            <NavLink
                                href="/guide/save-images"
                                linkName={'Save images'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/create-note"
                                linkName={'Create notes/to-dos'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/reminders"
                                linkName={'Add reminders'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/combine"
                                linkName={'Combine items'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/save-text-snippet"
                                linkName={'Save text-snippets'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/search"
                                linkName={'Search'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/bookmarks"
                                linkName={'Import bookmarks'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/settings"
                                linkName={'Settings'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/custom-emojis"
                                linkName={'Custom Emojis'}
                                mobile={true}
                            ></NavLink>
                            <NavLink
                                href="/guide/bin"
                                linkName={'Bin'}
                                mobile={true}
                            ></NavLink>
                            <div style={{ minWidth: '12px', height: '1px' }} />
                        </div>
                        <div
                            style={{
                                position: 'absolute',
                                right: '0',
                                width: '22px',
                                height: '100%',
                                zIndex: '104',
                                top: '0',
                                backgroundImage:
                                    'linear-gradient(90deg, rgba(244,245,247,0.00) 0%, #F4F5F7 100%)'
                            }}
                        />
                    </div>
                    <div className="columns sideBar">
                        <div className="column is-one-quarter is-hidden-touch">
                            <aside
                                className="menu"
                                style={{
                                    position: 'sticky',
                                    top: '16px',
                                    zIndex: '90'
                                }}
                            >
                                <p className="menu-label">intro</p>
                                <ul className="menu-list">
                                    <li>
                                        <NavLink
                                            href="/guide/getting-started"
                                            linkName={'Getting started'}
                                            sublinks={[
                                                { title: 'Installation' },
                                                { title: 'Permissions' }
                                            ]}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/overview"
                                            linkName={'Overview'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/shortcuts"
                                            linkName={'Shortcuts'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/create-account"
                                            linkName={'Create account'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/manage-account"
                                            linkName={'Manage account'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/popover"
                                            linkName={'The Popover'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/mobile"
                                            linkName={'Mobile app'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/trial-saves"
                                            linkName={'Trial plan'}
                                        ></NavLink>
                                    </li>
                                </ul>
                                <p className="menu-label">Groups</p>
                                <ul className="menu-list">
                                    <li>
                                        <NavLink
                                            href="/guide/create-new-group"
                                            linkName={'Create new group'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/edit-group"
                                            linkName={'Edit group'}
                                            sublinks={[
                                                { title: 'Change Emoji' },
                                                { title: 'Change Position' }
                                            ]}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/open-all-sites"
                                            linkName={'Open all sites'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/group-options"
                                            linkName={'Group options'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/categories"
                                            linkName={'Categories'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/share-categories"
                                            linkName={'Share Categories'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/workspaces"
                                            linkName={'Workspaces'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/share-workspaces"
                                            linkName={'Share workspaces'}
                                        ></NavLink>
                                    </li>
                                </ul>
                                <p className="menu-label">Items</p>
                                <ul className="menu-list">
                                    <li>
                                        <NavLink
                                            href="/guide/save-tabs"
                                            linkName={'Save tabs'}
                                            sublinks={[
                                                {
                                                    title: 'Save all tabs in window'
                                                },
                                                {
                                                    title: 'Right-click any site'
                                                }
                                            ]}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/save-images"
                                            linkName={'Save images'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/create-note"
                                            linkName={'Create notes/to-dos'}
                                            sublinks={[
                                                { title: 'Toolbar' },
                                                { title: 'Create bullet list' }
                                            ]}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/reminders"
                                            linkName={'Add reminders'}
                                            sublinks={[
                                                { title: 'Reminders on sites' },
                                                { title: 'Reminders on notes' }
                                            ]}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/combine"
                                            linkName={'Combine items'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/save-text-snippet"
                                            linkName={'Save text-snippets'}
                                        ></NavLink>
                                    </li>
                                </ul>
                                <p className="menu-label">Other</p>
                                <ul className="menu-list">
                                    <li>
                                        <NavLink
                                            href="/guide/bookmarks"
                                            linkName={'Import bookmarks'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/search"
                                            linkName={'Search'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/settings"
                                            linkName={'Settings'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/custom-emojis"
                                            linkName={'Custom Emojis'}
                                        ></NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            href="/guide/bin"
                                            linkName={'Bin'}
                                        ></NavLink>
                                    </li>
                                </ul>
                            </aside>
                        </div>
                        <div
                            className="column"
                            style={{
                                paddingRight: '1rem',
                                paddingBottom: '8rem'
                            }}
                        >
                            {children}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
